import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import { Code, Palette, Zap, Users } from "lucide-react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const About: React.FC = () => {
  const bg = useColorModeValue("white", "gray.800");
  const headingColor = useColorModeValue("gray.800", "white");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const bodyTextColor = useColorModeValue("gray.700", "gray.300");
  const featureBg = useColorModeValue("gray.50", "gray.700");
  const featureBorderColor = useColorModeValue("gray.100", "gray.600");
  const featureHoverBorderColor = useColorModeValue("brand.200", "brand.400");

  const features = [
    {
      icon: Code,
      title: "Clean Code",
      description:
        "Writing maintainable, scalable, and efficient code following best practices.",
    },
    {
      icon: Palette,
      title: "UI/UX Design",
      description:
        "Creating beautiful, intuitive interfaces that provide exceptional user experiences.",
    },
    {
      icon: Zap,
      title: "Performance",
      description:
        "Optimizing applications for speed, accessibility, and cross-browser compatibility.",
    },
    {
      icon: Users,
      title: "Collaboration",
      description:
        "Working effectively in teams using modern development workflows and tools.",
    },
  ];

  return (
    <Box id="about" py={20} bg={bg}>
      <Container maxW="7xl">
        <MotionBox
          textAlign="center"
          mb={16}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Heading
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="bold"
            color={headingColor}
            mb={4}
          >
            About Me
          </Heading>
          <Text fontSize="xl" color={textColor} maxW="3xl" mx="auto">
            Dynamic Frontend Developer with 5+ years of experience building
            scalable web and mobile apps using React, Next.js, and React Native.
            Skilled in crafting high-quality UIs, collaborating
            cross-functionally, and optimizing performance through clean code
            and seamless backend integration.
          </Text>
        </MotionBox>

        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          spacing={12}
          alignItems="start"
        >
          <MotionBox
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <VStack align="start" spacing={6}>
              <Text fontSize="lg" color={bodyTextColor} lineHeight="tall">
                My journey in web development started with a curiosity about how
                websites work. Over the years, I've evolved from writing basic
                HTML and CSS to crafting complex, interactive applications using
                cutting-edge technologies like React, Next.js, and React Native.
              </Text>
              <Text fontSize="lg" color={bodyTextColor} lineHeight="tall">
                I believe in the power of continuous learning and staying
                updated with the latest industry trends. Currently based in
                Ibadan, Nigeria, I work with teams globally to deliver
                exceptional digital experiences that make a real impact.
              </Text>
              <HStack spacing={4} flexWrap="wrap">
                <Text fontWeight="semibold" color="brand.500">
                  Current Focus:
                </Text>
                <Text color={textColor}>
                  React • Next.js • React Native • TypeScript • Chakra UI
                </Text>
              </HStack>
            </VStack>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={6}>
              {features.map((feature, index) => (
                <MotionBox
                  key={feature.title}
                  p={6}
                  bg={featureBg}
                  borderRadius="xl"
                  border="1px"
                  borderColor={featureBorderColor}
                  _hover={{
                    borderColor: featureHoverBorderColor,
                    transform: "translateY(-4px)",
                    boxShadow: "xl",
                  }}
                  transition="all 0.3s ease"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  // transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Icon
                    as={feature.icon}
                    w={8}
                    h={8}
                    color="brand.500"
                    mb={4}
                  />
                  <Heading size="md" mb={2} color={headingColor}>
                    {feature.title}
                  </Heading>
                  <Text color={textColor} fontSize="sm">
                    {feature.description}
                  </Text>
                </MotionBox>
              ))}
            </SimpleGrid>
          </MotionBox>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default About;
